import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useMarkInterestShift } from "@src/appV2/OpenShifts/ShiftAction/api/useMarkInterestShift";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { LoadingButton } from "../../components/LoadingButton";
import { invalidateShifts } from "../../invalidateShifts";
import { useCreateBookingRequest } from "../../WorkerShifts/BookingRequests/api/useCreateBookingRequest";
import { useGetBookingRequests } from "../../WorkerShifts/BookingRequests/api/useGetBookingRequests";
import { useWorkplaceHasBookingRequestsEnabled } from "../../WorkerShifts/BookingRequests/api/useWorkplaceHasBookingRequestsEnabled";
import { BookingRequestStatus } from "../../WorkerShifts/BookingRequests/types";
import { createBackgroundCheckUrlFromMissingDataTypes } from "../createBackgroundCheckUrlFromMissingDataTypes";

interface MarkInterestButtonProps {
  shiftId: string;
  workplaceId: string;
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
  offerId?: string;
}

export function MarkInterestButton(props: MarkInterestButtonProps) {
  const { shiftId, workplaceId, offerId, missingBackgroundCheckDataTypes } = props;
  const queryClient = useQueryClient();
  const workerId = useDefinedWorker().userId;
  const {
    bookingRequestsEnabled: shouldShowBookingRequests,
    isLoading: isLoadingBookingRequestsEnabled,
  } = useWorkplaceHasBookingRequestsEnabled(workplaceId);
  const { showSuccessToast } = useToast();
  const { data: bookingRequests, refetch: refetchBookingRequests } = useGetBookingRequests(
    {
      filter: {
        status: { eq: BookingRequestStatus.PENDING },
        "worker.id": { eq: workerId },
        "shift.id": { eq: shiftId },
      },
    },
    {
      enabled: shouldShowBookingRequests,
    }
  );

  const { mutate: markInterest, isLoading: isMarkingInterest } = useMarkInterestShift({
    onSuccess: async () => {
      logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_MARK_INTEREST, {
        shiftId,
      });
      showSuccessToast("Approval has been requested.");
      await invalidateShifts(queryClient);
    },
    useErrorBoundary: false,
    meta: {
      userErrorMessage: "Error requesting approval!",
    },
  });

  const { mutate: createBookingRequest, isLoading: isCreatingBookingRequest } =
    useCreateBookingRequest({
      onSuccess: async (response) => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_CREATE_BOOKING_REQUEST, {
          shiftId,
          offerId,
          workplaceId,
          workerId,
          bookingRequestId: response.data.id,
        });

        await invalidateShifts(queryClient);
        await refetchBookingRequests();
      },
      useErrorBoundary: false,
      meta: {
        userSuccessMessage: "Shift has been requested.",
        userErrorMessage: "Error requesting shift!",
      },
    });
  const history = useHistory();

  const isLoading =
    isMarkingInterest || isCreatingBookingRequest || isLoadingBookingRequestsEnabled;

  const shiftHasAlreadyBeenRequested =
    shouldShowBookingRequests && (!offerId || (bookingRequests?.data?.length ?? 0) > 0);

  return (
    <LoadingButton
      size="large"
      variant="contained"
      isLoading={isLoading}
      disabled={shiftHasAlreadyBeenRequested}
      onClick={async () => {
        if (missingBackgroundCheckDataTypes.length > 0) {
          history.push(
            createBackgroundCheckUrlFromMissingDataTypes({
              queryParams: new URLSearchParams(history.location.search),
              shiftId,
              missingBackgroundCheckDataTypes,
            })
          );
          return;
        }

        if (shouldShowBookingRequests && offerId) {
          createBookingRequest({ shiftId, offerId, workerId });
        } else {
          markInterest({ shift: shiftId });
        }
      }}
    >
      {shouldShowBookingRequests && shiftHasAlreadyBeenRequested && "Shift requested"}
      {shouldShowBookingRequests && !shiftHasAlreadyBeenRequested && "Request this shift"}
      {!shouldShowBookingRequests && "Request approval"}
    </LoadingButton>
  );
}
